import React, { useMemo, useState } from 'react'
import { Menu } from 'antd'
import Link from 'next/link'

import { useTranslation } from 'next-i18next'
import { getUser } from 'src/libs/browser'
import { getInfo, hasRequiredItem } from 'src/libs/util'
import { URL_TEMPLATE, USER_ROLES_ENUM } from 'src/libs/constant'
import { useTracking } from 'src/hooks/useTracking'
import { TRACKING_EVENT_NAME } from 'src/libs/constant/tracking'
import { groupBy } from 'lodash'
import useGetWithContext from 'src/hooks/useGetWithContext'
import userApi from 'src/api/userApi'
import { SCHEDULE_TYPE } from 'src/libs/constant/calendar'
import { PERMISSION_ROLES } from './sidebarPermissions'
import { menuSidebarConfig } from './sidebarMenuConfig'

const ICON_BASE_PATH = '/image/ui-kit/sidebar'

const groupKeys = [
  'student',
  'operate',
  'tutor',
  'administration'
]

const { SubMenu } = Menu

const isVisible = (item, permissions) => {
  if (item.permission) return !!permissions[item.permission]
  if (item.permissions) return item.permissions.some(p => permissions[p])
  return true
}

const renderIcon = (iconFile) => (
  <img style={{ width: '20px', height: '20px' }} src={`${ICON_BASE_PATH}/${iconFile}`} alt='icon' />
)

export function SidebarMenu ({ sidebarIsCollapsed }) {
  const { pathname } = window.location
  const user = getUser()
  const userRoles = user?.userRoles
  const { data: userDetail } = useGetWithContext(
    `GET_USER_DETAIL_BY_ID${user.userId}`,
    userApi.detail,
    user.userId,
    { isDisabled: !user.userId }
  )
  const { classType } = getInfo(userDetail?.userInfoData)

  const permissions = useMemo(() => {
    const perms = Object.fromEntries(
      Object.entries(PERMISSION_ROLES).map(([key, roles]) => [key, hasRequiredItem(userRoles, roles)])
    )
    const isTeacherWithBackupOrRegular =
      hasRequiredItem(userRoles, [USER_ROLES_ENUM.EMPLOYEE_TEACHER]) &&
      classType?.some(item => [SCHEDULE_TYPE.BACKUP, SCHEDULE_TYPE.REGULAR].includes(item))
    perms.canUseStudentMenu = perms.canUseStudentMenu || isTeacherWithBackupOrRegular
    perms.canUseTeacherSalary = perms.canUseTeacherSalary || isTeacherWithBackupOrRegular
    return perms
  }, [user])

  const { onTitleClick, opened } = useControlOpenedMenu(sidebarIsCollapsed)
  const defaultSelectedKeys = []
  const { t } = useTranslation()

  const { handleTracking } = useTracking()

  const menuSidebar = useMemo(() => {
    return menuSidebarConfig.map(item => ({
      ...item,
      icon: renderIcon(item.icon),
      subMenu: item.subMenu?.map(sub => ({ ...sub }))
    }))
  }, [])

  const getMenu = () => {
    const menuGroup = groupBy(menuSidebar, item => item.groupKey)
    const menuGroupByValid = groupKeys.reduce((acc, curr) => {
      const submenu = menuGroup?.[curr].filter(itemMenu => isVisible(itemMenu, permissions))
      return [
        ...acc,
        submenu.length && curr
      ].filter(item => item)
    }, [])

    return menuGroupByValid.map(groupKey => {
      return (
        <Menu.ItemGroup key={groupKey} title={t(`common.menu_group.${groupKey}`)}>
          {getItem(menuGroup[groupKey])}
        </Menu.ItemGroup>
      )
    })
  }

  const getItem = (menu) => {
    if (!menu) return null
    return menu.map((item) => {
      const { title, subMenu, icon, link } = item
      const key = item.key || item.link
      if (isVisible(item, permissions)) {
        if (link) {
          if (pathname.startsWith(key)) {
            defaultSelectedKeys.push(key)
          }

          const handleClickMenu = ({ key }) => {
            switch (key) {
              case URL_TEMPLATE.SESSION_TEACHING_SCHEDULE:
                return handleTracking(TRACKING_EVENT_NAME.NUMBER_CLICKS_TODAYCLASSSCHEDULE)
              default:
                return <></>
            }
          }
          return (
            <Menu.Item onClick={handleClickMenu} key={key} className='lms-menu'>
              <Link href={link}>
                <a
                  title={t(title)}
                  style={{ fontSize: '15px', fontWeight: '500', lineHeight: '16px', color: '#4B5563' }}
                >
                  {t(title)}
                </a>
              </Link>
            </Menu.Item>
          )
        } else {
          return (
            <SubMenu
              key={key}
              icon={icon}
              title={t(title)}
              className='custom-submenu'
              onTitleClick={onTitleClick.bind(null, key)}
            >
              {getItem(subMenu)}
            </SubMenu>
          )
        }
      }
      return null
    })
  }
  return (
    <Menu
      mode='inline'
      defaultSelectedKeys={defaultSelectedKeys}
      selectedKeys={defaultSelectedKeys}
      openKeys={opened}
      className={sidebarIsCollapsed ? 'menu-collapsed' : ''}
      expandIcon={({ isOpen }) => (
        <img
          alt='icon'
          src={`/image/ui-kit/sidebar/arrow-${isOpen ? 'up' : 'down'}.png`}
          style={{ width: '16px' }}
        />
      )}
    >
      {getMenu()}
    </Menu>
  )
}

const useControlOpenedMenu = (sidebarIsCollapsed) => {
  const { pathname } = window.location
  const matchOpen = pathname.match(/(^\/[^/]+)/)
  const [openedKey, setOpenKey] = useState([matchOpen && matchOpen[0]])
  const onTitleClick = (key) => {
    if (!sidebarIsCollapsed) {
      if (openedKey[0] !== key) {
        setOpenKey([key])
      } else {
        setOpenKey([])
      }
    }
  }
  return {
    opened: sidebarIsCollapsed ? undefined : openedKey,
    onTitleClick
  }
}
